import React, { useEffect, useState, useRef } from "react";
import { ArrowDropDown, ArrowDropUp, Clear, DoneAll } from '@mui/icons-material';
import { Box, TextField, IconButton, FormControl, Popover, InputAdornment, Button, Divider } from "@mui/material";
import { TreeItem2, RichTreeView } from "@mui/x-tree-view";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useDispatch } from "react-redux";
import { cloneDeep, isEmpty, isNil, orderBy } from "lodash";
import { useTranslation } from "react-i18next";
import { useTreeViewApiRef } from '@mui/x-tree-view/hooks';
import codeUtil from "@plannel/pages/utils/code-util";

import locationService from "@plannel/services/data/location-service";
import BODService from "@plannel/services/data/bod-service";
import siteService from "@plannel/services/data/site-master-service";

const REDUX_LOCATION_TREE_FILTER = "LOCATION_TREE_FILTER";
const LOCATION_TYPE_MAP = codeUtil.locationType;
const SITE = "SITE";

const LOCATION_TYPE = {
  PLANT: LOCATION_TYPE_MAP.PLANT.cd,
  DC: LOCATION_TYPE_MAP.DC.cd,
  WHSE: LOCATION_TYPE_MAP.WHSE.cd,
  STO: LOCATION_TYPE_MAP.STO.cd,
  CUST: LOCATION_TYPE_MAP.CUST.cd,
  VENDOR: LOCATION_TYPE_MAP.VENDOR.cd,
  VIRTUAL_DC: LOCATION_TYPE_MAP.VIRTUAL_DC.cd
}

const MP_LOCATION_TYPE = [
  LOCATION_TYPE.PLANT
]

const IP_LOCATION_TYPE = [
  LOCATION_TYPE.DC,
  LOCATION_TYPE.WHSE,
  LOCATION_TYPE.STO,
  LOCATION_TYPE.CUST,
  LOCATION_TYPE.VENDOR,
  LOCATION_TYPE.PLANT
]


/* DC, WHSE, STO, CUST, VENDOR, in-house true */
const IPLocationTreeFilter = ({ viewName, filterValue, onChangeFilter, disabled, addLocType = [] }) => {
  return <LocationTreeFilter viewName={viewName} filterValue={filterValue} onChangeFilter={onChangeFilter} locTypeFilters={[...IP_LOCATION_TYPE, ...addLocType]} inhouseFilters={[true]} disabled={disabled}/>
}

const MPLocationTreeFilter = ({ viewName, filterValue, onChangeFilter, disabled }) => {
  return <LocationTreeFilter viewName={viewName} filterValue={filterValue} onChangeFilter={onChangeFilter} locTypeFilters={MP_LOCATION_TYPE} disabled={disabled}/>
}

const LocationTreeFilter = ({ viewName, filterValue, onChangeFilter, locTypeFilters, inhouseFilters, title = "locations", disabled, styles }) => {
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const locationDataListReduxState = reduxUtil.getViewState(REDUX_LOCATION_TREE_FILTER);
  const [locationDataList, setLocationDataList] = useState(locationDataListReduxState?.[title] ?? []);
  const [locationFilterDataList, setLocationFilterDataList] = useState(title === "originLocation" || title === "destLocation" ? locationDataListReduxState?.[title] ?? [] : []);
  const [siteDataList, setSiteDataList] = useState(locationDataListReduxState?.site ?? []);
  const [hrchyCdText, setHrchyCdText] = useState(reduxUtil.getViewState(viewName)?.filter?.[title]?.name ? reduxUtil.getViewState(viewName)?.filter?.[title]?.name : reduxUtil.getViewState(viewName)?.filter?.[title]?.map(location => {
    return location?.name
  }).join(',') ?? "");
  const [loading, setLoading] = useState(false);

  const [defaultVal, setDefaultVal] = useState(filterValue ? cloneDeep(filterValue) : []);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const locationMap = useRef({});

  const [checkSelected, setCheckSelected] = useState(reduxUtil.getViewState(viewName)?.filter?.locCheckSelected || []);

  const open = Boolean(anchorEl);
  const inputRef = useRef(null);
  const apiRef = useTreeViewApiRef();

  /* Location Type 정렬 PLANT -> DC -> WHSE -> STO -> CUST -> VENDOR */
  const sortLocationResults = (locResults) => {
    const plantLocations = [];
    const dcLocations = [];
    const whseLocations = [];
    const stoLocations = [];
    const custLocations = [];
    const vendorLocations = [];
    const typeNotFound = [];

    for (const loc of locResults) {
      const { locType } = loc;

      switch (locType) {
        case LOCATION_TYPE_MAP.PLANT.cd:
          plantLocations.push(loc);
          break;
        case LOCATION_TYPE_MAP.DC.cd:
          dcLocations.push(loc);
          break;
        case LOCATION_TYPE_MAP.WHSE.cd:
          whseLocations.push(loc);
          break;
        case LOCATION_TYPE_MAP.STO.cd:
          stoLocations.push(loc);
          break;
        case LOCATION_TYPE_MAP.CUST.cd:
          custLocations.push(loc);
          break;
        case LOCATION_TYPE_MAP.VENDOR.cd:
          vendorLocations.push(loc);
          break;
        case LOCATION_TYPE_MAP.VIRTUAL_DC.cd:
          dcLocations.push(loc);
          break;
        default:
          typeNotFound.push(loc);
          break;
      }
    }

    return [...plantLocations, ...dcLocations, ...whseLocations, ...stoLocations, ...custLocations, ...vendorLocations, ...typeNotFound];
  }

  const setLocationState = (selectList) => {
    const reduxViewState = reduxUtil.getViewState(viewName);
    const locationArray = [];

    const filterValue = reduxViewState?.filter ? reduxViewState.filter : {};
    const checkSelectedArray = selectList || checkSelected;

    checkSelectedArray.forEach(selectedNode => {
      const location = locationFilterDataList.filter(data => data && (data.locCd === selectedNode.locCd));
      if (location[0]) {
        locationArray.push(location[0]);
      }
    });

    if (filterValue[title] && filterValue[title].length === locationArray.length) {
      if (!locationArray.every((location, index) => location?.locCd === filterValue[title][index]?.locCd)) {
        onChangeFilter(({ ...filterValue, [title]: locationArray}));

        const reduxState = reduxUtil.createFilterViewState(viewName, ({ ...filterValue, [title]: locationArray }));
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      }
    } else {
      onChangeFilter(({ ...filterValue, [title]: locationArray }));

      const reduxState = reduxUtil.createFilterViewState(viewName, ({ ...filterValue, [title]: locationArray }));
      reduxDispatch(reduxUtil.updateViewState(reduxState));
    }

    setCheckSelected(checkSelectedArray);
    if (locationArray.length === 0) {
      setHrchyCdText("");
      setDefaultVal([]);
    } else {
      const locCdTextValue = locationArray.map(location => {
        return location?.name
      }).join(',');
      setHrchyCdText(locCdTextValue);
    }
  }

  const handleClick = (event) => {
    if (disabled) {
      return;
    }
    
    inputRef.current.focus()
    setAnchorEl(event.currentTarget);
  };

  /* PLANT, CDC, RDC, DC, VENDOR */
  const locationTypeFilter = (results) => {
    let resultsFilter = [];

    if (!isEmpty(locTypeFilters) || !isEmpty(inhouseFilters)) {
      for (const result of results) {
        if (!isEmpty(locTypeFilters) && !locTypeFilters.includes(result.locType)) {
          continue;
        }
        if (!isEmpty(inhouseFilters) && !inhouseFilters.includes(result.inhouseFlg)) {
          continue;
        }
        resultsFilter.push(result);
      }
    } else {
      resultsFilter = results;
    }

    /* 기억 하고있는 redux data가 제거되거나 변경되는경우 dropdown list에 데이터가 존재하는지 체크하는 변수 */
    // const existSelectedValueInResults = resultsFilter.find(result => isEqual(result.locCd, locationValue?.locCd));

    // if (isEmpty(existSelectedValueInResults)) {
    //   onChangeHandler(null, null);
    // } else {
    //   onChangeHandler(null, existSelectedValueInResults);
    // }

    return sortLocationResults(resultsFilter);
  }

  const setFilteredDestLocations = (results) => {
    const originLocationMap = new Map();
    const destLocationMap = new Map();

    for (const result of results) {
      if (!originLocationMap.has(result.originLocation?.locCd)) {
        result.originLocation.site = result.originSite;
        originLocationMap.set(result.originLocation.locCd, result.originLocation);
      }

      if (!destLocationMap.has(result.destLocation?.locCd)) {
        result.destLocation.site = result.destSite;
        destLocationMap.set(result.destLocation?.locCd, result.destLocation);
      }
    }

    const originLocationList = orderBy(Array.from(originLocationMap.values()), ["name"], ["asc"]);
    const destLocationList = orderBy(Array.from(destLocationMap.values()), ["name"], ["asc"]);

    if (title === "originLocation") {
      setLocationFilterDataList(originLocationList)
    } else if (title === "destLocation") {
      setLocationFilterDataList(destLocationList)
    }

    const createLocationDataListReduxState = reduxUtil.createObjectState(REDUX_LOCATION_TREE_FILTER, { "originLocation": originLocationList, "destLocation": destLocationList });
    reduxDispatch(reduxUtil.updateViewState(createLocationDataListReduxState));
  }

  const retrieveOriginDestLocations = () => {
    if (isEmpty(locationDataListReduxState?.[title])) {
      BODService.getLookup()
      .then(response => {
        const { results } = response.data;
        setFilteredDestLocations(results);
      })
      .catch(e => {
        debug(e);
      })
    } 
  }

  const getLocationData = () => {
    /* 이전에 불러온 Dropdown data list가 존재하는경우 다시 가져오지 않음. */
    if (isEmpty(locationDataListReduxState?.[title])) {
      let locationResultData = [];
      const sites = [];
      locationService.getLookup()
        .then(response => {
          const { results } = response.data;
          if (!isEmpty(results)) {
            locationResultData = results;
          }
          const filterLocationList = locationTypeFilter(locationResultData);
          setLocationFilterDataList(filterLocationList);
          setLocationDataList(locationResultData);
        })
        .catch(e => {
          debug(e);
        })
        .then(() => {
          const createLocationDataListReduxState = reduxUtil.createObjectState(REDUX_LOCATION_TREE_FILTER, { [title]: locationResultData, site: sites });
          reduxDispatch(reduxUtil.updateViewState(createLocationDataListReduxState));
        });
    } else {
      setLocationFilterDataList(locationTypeFilter(locationDataList));
    }
  }

  const retrieveSiteData = () => {
    if (isEmpty(locationDataListReduxState?.site)) {
      const sites = [];
      siteService.getLookUp()
        .then(response => {
          const { results } = response.data;
          if (!isEmpty(results)) {
            for (const site of results) {
              sites.push(site);
            }
            setSiteDataList(results);
          }
        })
        .catch(e => {
          debug(e);
        })
        .then(() => {
          const createLocationDataListReduxState = reduxUtil.createObjectState(REDUX_LOCATION_TREE_FILTER, { site: sites });
          reduxDispatch(reduxUtil.updateViewState(createLocationDataListReduxState));
        });
    } 
  }


  const onItemSelectionToggle = (_, node, selected) => {
    let selectedItem = cloneDeep(defaultVal);
    const toggleSelected = () => {
      if (selected) {
        selectedItem.push(node)
      } else {
        const idx = selectedItem.indexOf(node);
        if (idx < 0) {
          return;
        }
        selectedItem.splice(idx, 1)
      }
    }

    // 상위 site를 클릭 했을 때 하위 children도 같이 값을 맞춰줘야 함.
    if (locationMap?.current[node]?.isSite) {
      const child = locationMap?.current[node]?.children.map(val => val.id);
      if (selected) {
        selectedItem = [...selectedItem, node, ...child];
      } else {
        for (const remove of [node, ...child]) {
          const idx = selectedItem.indexOf(remove);
          if (idx < 0) {
            continue;
          }
          selectedItem.splice(idx, 1)
        }
      }
    } else {
      // 하위 두개가 모두 선택 시 상위도 선택해줘야 함 
      const selectLoc = locationFilterDataList.filter(value => value.locCd === node);
      if (selectLoc !== null) {
        const key = `${selectLoc[0]?.site?.siteCd}_${SITE}`
        const children = locationMap.current?.[key] ?? null;
        if (selected) {
          let isAllSelectdChild = true;
          if (children) {
            for (const child of children?.children) {
              if (!defaultVal.includes(child.id) && child.id !== node) {
                isAllSelectdChild = false;
              }
            }
            if (children?.children && isAllSelectdChild) {
              selectedItem = [...selectedItem, key, ...children?.children.map(val => val.id)];
            } else {
              toggleSelected();
            }
          } else {
            toggleSelected();
          }

        } else {
          let isAllNotSelectdChild = true;
          if (children) {
            for (const child of children?.children) {
              if (defaultVal.includes(child.id) && child.id !== node) {
                isAllNotSelectdChild = false;
              }
            }
            if (children?.children && isAllNotSelectdChild) {
              for (const remove of [key, ...children?.children.map(val => val.id)]) {
                const idx = selectedItem.indexOf(remove);
                if (idx < 0) {
                  continue;
                }
                selectedItem.splice(idx, 1)
              }
            } else {
              toggleSelected();
            }
          } else {
            toggleSelected();
          }
        }
      }
    }
    setDefaultVal([...new Set(selectedItem)])
    const checkedArray = locationFilterDataList.filter(value => selectedItem.some(obj => obj === value.locCd));
    setCheckSelected(checkedArray);
  }

  const handleClose = () => {
    setLocationState();
    setAnchorEl(null);
  };

  const handleMouseEnter = () => {
    if (disabled) {
      return;
    }
    setIsFocused(true);
  };

  const handleMouseLeave = () => {
    if (disabled) {
      return;
    }

    setIsFocused(false);
  };

  const handleTreeItem = (event, props) => {
    // parentId를 선택했을 시에는 하위도 같이 선택해줘야 함 
    if (props.children?.props?.itemsToRender) {
      let array = cloneDeep(defaultVal);
      const child = props.children?.props?.itemsToRender.map(value => value.itemId);
      if (!defaultVal.includes(props.itemId)) {
        array = [...defaultVal, props.itemId, ...child];
      } else {
        for (const remove of [props.itemId, ...child]) {
          const idx = array.indexOf(remove);
          if (idx < 0) {
            continue;
          }
          array.splice(idx, 1)
        }
      }
      const checkedArray = locationFilterDataList.filter(value => array.some(obj => obj === value.locCd));
      setCheckSelected(checkedArray);
      setDefaultVal([...new Set(array)])
    } else {
      // 하위 두개가 모두 선택 시 상위도 선택해줘야 함 
      const selectLoc = locationFilterDataList.filter(value => value.locCd === props.itemId);
      if (selectLoc !== null) {
        const key = `${selectLoc[0]?.site?.siteCd}_${SITE}`
        let array = cloneDeep(defaultVal); 
        const children = locationMap.current?.[key] ?? null;
        if (!defaultVal.includes(props.itemId)) {
          let isAllSelectdChild = true;
          if (children) {
            for (const child of children?.children) {
              if (!defaultVal.includes(child.id) && child.id !== props.itemId) {
                isAllSelectdChild = false;
              }
            }
            if (children?.children && isAllSelectdChild) {
              array = [...array, key, ...children?.children.map(val => val.id)];
              const checkedArray = locationFilterDataList.filter(value => array.some(obj => obj === value.locCd));
              setCheckSelected(checkedArray);
              setDefaultVal([...new Set(array)])
            } else {
              apiRef.current?.selectItem({ event, itemId: props.itemId, keepExistingSelection: true });
            }
          } else {
            apiRef.current?.selectItem({ event, itemId: props.itemId, keepExistingSelection: true });
          }
        } else {
          //하위가 체크를 안했을 때 상위도 체크 제외 
          let isAllNotSelectdChild = true;
          if (children) {
            const array = cloneDeep(defaultVal);
            for (const child of children?.children) {
              if (defaultVal.includes(child.id) && child.id !== props.itemId) {
                isAllNotSelectdChild = false;
              }
            }
            if (children?.children && isAllNotSelectdChild) {
              for (const remove of [key, ...children?.children.map(val => val.id)]) {
                const idx = array.indexOf(remove);
                if (idx < 0) {
                  continue;
                }
                array.splice(idx, 1)
              }
              const checkedArray = locationFilterDataList.filter(value => array.some(obj => obj === value.locCd));
              setCheckSelected(checkedArray);
              setDefaultVal(array)
            } else {
              apiRef.current?.selectItem({ event, itemId: props.itemId, keepExistingSelection: true });
            }
          } else {
            apiRef.current?.selectItem({ event, itemId: props.itemId, keepExistingSelection: true });
          }
        }
      }
    }
  }

  const renderTree = () => {
    const locObj = {};
    const hasSiteList = [];
    for (const loc of locationFilterDataList) {
      let child = [];
      const data = {};
      data.id = loc.locCd;
      data.label = loc.name;
      let key = loc.locCd;
      let isSite = false;
      if (loc.site) {
        key = `${loc.site?.siteCd}_${SITE}`;
        isSite = true;
        hasSiteList.push(loc.site);
      }
      if (locObj[key]?.children) {
        child = locObj[key].children;
      }
      child.push(data);
      const info = {};
      info.children = child;
      info.isSite = isSite;
      locObj[key] = info;
    }

    const groupResult = [];
    const locResult = [];
    for (const loc of Object.keys(locObj)) {
      const data = {};
      if (locObj[loc].isSite && siteDataList !== null && !isEmpty(siteDataList)) {
    //  el.siteCd
        const site = hasSiteList?.filter(el => el && (`${el.siteCd}_${SITE}` === loc));
        if (!site || isEmpty(site)) continue;

        data.id = `${site[0].siteCd}_${SITE}`;
        data.itemId = `${site[0].siteCd}_${SITE}`;
        data.label = site[0].name;
        data.children = locObj[loc].children;
        groupResult.push(data);
      } else {
        const location = locObj[loc].children[0];
        data.id = location.id;
        data.itemId = location.id;
        data.label = location.label;
        locResult.push(data);
      }
    }
    locationMap.current = locObj;
    return [...groupResult, ...locResult];
  }

  const resetFilter = () => {
    setHrchyCdText("");
    setCheckSelected([]);
    setLocationState([]);
    setLoading(true);
  }
  
  useEffect(() => {
    if (!isEmpty(locationFilterDataList)) {
      setLoading(true);
    }
       // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationFilterDataList])

  useEffect(() => {
    if (title === "locations" || title === "vendorLocation") {
      getLocationData();
    } else {
      retrieveOriginDestLocations()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteDataList])

  useEffect(() => {
    retrieveSiteData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isNil(filterValue) || isEmpty(filterValue)) {
      setDefaultVal([]);
      setHrchyCdText("");
      setCheckSelected([]);
      setLocationState([]);
    } else {
      if (loading && !isEmpty(filterValue)) {
        const value = cloneDeep(filterValue);
        for (const site of siteDataList)  {
          const key = `${site.siteCd}_${SITE}`;
          if (locationMap?.current[key] && locationMap?.current[key]?.isSite) {
            const child = locationMap?.current[key]?.children.map(val => val.id);
            if (child.every((val) => filterValue.includes(val))) {
              value.push(key);
            }
          }
        }

        setDefaultVal(value);
        const checkedArray = locationFilterDataList.filter(val => value.some(obj => obj === val.locCd));
        setCheckSelected(checkedArray);
        setHrchyCdText(checkedArray?.map(location => location?.name).join(',') ?? "");

        const reduxViewState = reduxUtil.getViewState(viewName);
        const reduxState = reduxUtil.createFilterViewState(viewName, ({ ...reduxViewState?.filter, [title]: checkedArray }));
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      } 
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, loading])

  const CustomTreeItem = React.forwardRef(function CustomTreeItem(props, ref) {
    return (
      <TreeItem2
        {...props}
        sx={{ boxSizing: "border-box" }}
        label=
        {
          <Box className={"MuiAutocomplete-popper MuiAutocomplete-noOptions"}
            sx={{
              padding: '3px 4px',
            }}
            onClick={e => { handleTreeItem(e, props); }}
          >{props.label}</Box>
        }
        ref={ref}
      />
    );
  });

  return (
    <>
      <FormControl
        className={"customer-hrchy-tree-filter"}
        size="small"
        sx={{ mr: 1, minWidth: 230, width: 240, ...styles }}
      >
        <TextField
          size="small"
          label={t(title)}
          type="string"
          onClick={(e) => handleClick(e)}
          onMouseEnter={() => handleMouseEnter()}
          onMouseLeave={() => handleMouseLeave()}
          disabled={disabled}
          value={hrchyCdText}
          onChange={(event) => setHrchyCdText(event.target.value)}
          inputRef={inputRef}
          autoComplete="off"
          InputProps={{
            endAdornment: hrchyCdText?.length > 0 ? (
              <>
                {isFocused ? <IconButton size="small" aria-label="clear text" disabled={disabled} onClick={(e) => {
                  e.stopPropagation(); // 부모 onClick 호출 방지
                  resetFilter();
                }}>
                  <Clear sx={{ fontSize: '1.2rem' }} />
                </IconButton> : null}
                <InputAdornment position="end">
                  <IconButton aria-label="search" sx={{ padding: 0 }} disabled={disabled} size="small">{open ? <ArrowDropUp />
                    : <ArrowDropDown />}</IconButton>
                </InputAdornment>
              </>
            ) : <InputAdornment position="end">
              <IconButton aria-label="search" size="small" disabled={disabled}>{open ? <ArrowDropUp />
                : <ArrowDropDown />}</IconButton>
            </InputAdornment>
          }}
        />
      </FormControl>
      <Popover open={Boolean(open)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        disableAutoFocus
        disableEnforceFocus
        TransitionProps={{ timeout: 0 }}
      >
        <Box>
          <Box sx={{ minWidth: 180, width: 240, overflowY: "auto", overflowX: "hidden", ...styles }}>
            <RichTreeView
              apiRef={apiRef}
              multiSelect checkboxSelection
              items={renderTree()}
              slots={{ item: CustomTreeItem }}
              selectedItems={defaultVal}
              onItemSelectionToggle={onItemSelectionToggle}
              sx={{
                maxHeight: 360, paddingLeft: "5px", paddoverflowY: "auto", minWidth: 230
              }}
            />
          </Box>
          <Box>
            <Divider sx={{ backgroundColor: "primary.main", borderColor: "primary.main", borderWidth: 0.5, opacity: 0.2 }} />
            <Box
              sx={{
                padding: '8px',
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
              <Button
                sx={{ mr: 1.1 }}
                variant="contained"
                startIcon={<DoneAll />}
                size="small"
                color="primary"
                onClick={handleClose} >
                {t("confirm")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  )
}

const reloadLocationTreeFilter = (reduxDispatch) => {
  const locationReduxFilterState = reduxUtil.getViewState(REDUX_LOCATION_TREE_FILTER);

  reduxDispatch(reduxUtil.removeViewState(locationReduxFilterState));
}

export default React.memo(LocationTreeFilter);
export { reloadLocationTreeFilter, IPLocationTreeFilter, MPLocationTreeFilter, LOCATION_TYPE };